import { motion } from 'framer-motion';
import { GlassCard } from '@/components/ui/GlassCard';
import { Code, User, Building, Layers, Palette, Rocket, Shield, Cloud, Zap } from 'lucide-react';

const expertiseAreas = [
  {
    id: 1,
    title: 'Création Web Pro',
    description: 'Sites vitrines, e-commerce et applications web sur mesure avec les dernières technologies.',
    icon: Code,
    features: ['Sites Vitrines', 'E-commerce', 'Web Apps', 'SEO Optimisé'],
    gradient: 'from-[#00f2fe] to-[#4facfe]',
  },
  {
    id: 2,
    title: 'Services Particuliers',
    description: 'Solutions digitales personnalisées pour freelances, artisans et entrepreneurs individuels.',
    icon: User,
    features: ['Portfolio', 'Blog Personnel', 'Landing Pages', 'Identité Visuelle'],
    gradient: 'from-[#4facfe] to-[#a855f7]',
  },
  {
    id: 3,
    title: 'Solutions Entreprises',
    description: 'Infrastructures robustes et scalables pour PME et grandes entreprises.',
    icon: Building,
    features: ['SaaS', 'ERP/CRM', 'Intranet', 'API & Intégrations'],
    gradient: 'from-[#a855f7] to-[#ec4899]',
  },
];

const additionalServices = [
  { icon: Layers, label: 'Architecture Modulaire' },
  { icon: Palette, label: 'Design UI/UX' },
  { icon: Rocket, label: 'Performance Optimale' },
  { icon: Shield, label: 'Sécurité Avancée' },
  { icon: Cloud, label: 'Cloud Native' },
  { icon: Zap, label: 'Temps Réel' },
];

export const Expertise = () => {
  return (
    <section
      id="expertise"
      data-testid="expertise-section"
      className="relative py-20 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4">
            Notre <span className="text-gradient">Expertise</span>
          </h2>
          <p className="text-white/60 text-base sm:text-lg max-w-2xl mx-auto">
            Des solutions adaptées à chaque besoin, de la startup au grand groupe.
          </p>
        </motion.div>

        {/* Main Expertise Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-16">
          {expertiseAreas.map((area, index) => (
            <GlassCard
              key={area.id}
              data-testid={`expertise-card-${area.id}`}
              delay={index * 0.15}
              className="text-center relative group"
            >
              {/* Icon with Glow */}
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                className={`w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${area.gradient} p-0.5`}
              >
                <div className="w-full h-full rounded-2xl bg-[#0a0f1e] flex items-center justify-center">
                  <area.icon className="w-8 h-8 text-white" />
                </div>
              </motion.div>

              {/* Title */}
              <h3 className="font-outfit text-xl sm:text-2xl font-semibold text-white mb-3">
                {area.title}
              </h3>

              {/* Description */}
              <p className="text-white/60 text-sm leading-relaxed mb-6">
                {area.description}
              </p>

              {/* Features List */}
              <ul className="space-y-2">
                {area.features.map((feature) => (
                  <li
                    key={feature}
                    className="text-white/50 text-sm flex items-center justify-center gap-2"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${area.gradient}`} />
                    {feature}
                  </li>
                ))}
              </ul>

              {/* Hover Glow Effect */}
              <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${area.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500 pointer-events-none`} />
            </GlassCard>
          ))}
        </div>

        {/* Additional Services */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <GlassCard hover={false} className="p-8 sm:p-12">
            <h3 className="font-outfit text-xl sm:text-2xl font-semibold text-white text-center mb-8">
              Technologies & Méthodologies
            </h3>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
              {additionalServices.map((service, index) => (
                <motion.div
                  key={service.label}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  whileHover={{ y: -5, scale: 1.05 }}
                  className="flex flex-col items-center gap-3 group cursor-default"
                >
                  <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-[#4facfe]/30 group-hover:shadow-[0_0_15px_rgba(79,172,254,0.2)] transition-all">
                    <service.icon className="w-6 h-6 text-[#4facfe]" />
                  </div>
                  <span className="text-white/60 text-xs font-medium text-center">
                    {service.label}
                  </span>
                </motion.div>
              ))}
            </div>
          </GlassCard>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mt-16"
        >
          <p className="text-white/60 mb-6">
            Prêt à donner vie à votre projet ?
          </p>
          <motion.button
            data-testid="cta-expertise-button"
            className="bg-gradient-to-r from-[#00f2fe] to-[#4facfe] text-white font-semibold rounded-lg px-8 py-4 text-base hover:opacity-90 transition-all shadow-[0_0_20px_rgba(79,172,254,0.4)]"
            whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(79, 172, 254, 0.5)' }}
            whileTap={{ scale: 0.98 }}
          >
            Discutons de votre projet
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Expertise;
